import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import HubLayout from '../common/HubLayout'
import InviteFamilyModal from './InviteFamilyModal'
import TreeNodeMenu from './TreeNodeMenu'
import MemberDetailPanel from './MemberDetailPanel'
import SecureLS from 'secure-ls'
import { axiosInstance } from '../../services/api'
import { API_CONFIG } from '../../config/api'
import { Search, Edit2, UserPlus, Users, GitBranch, Heart, Baby, UserCheck, Check } from 'lucide-react'
import { canInviteMembers, isAdmin } from '../../utils/permissions'
import FamilyTreeBalkan from './FamilyTreeBalkan'
import { getUser, setUser } from '../../utils/secureStorage'
import FamilyInviteList from './FamilyInviteList'
import HubInviteBanner from '../common/HubInviteBanner'
import { MemberAvatar, SpouseGhostCard, ChildCrossCard, HouseholdCallout } from './FamilyHubComponents'
import MobileHouseholdCard from './MobileHouseholdCard'
import MobileStatsSummary from './MobileStatsSummary'
import FamilyHubMembers from './FamilyHubMembers'

// Wrapper that renders the default Balkan FamilyTree JS with API integration
function FamilyTreeWrapper({ onPersonClick, onAddRelative, treeRef }) {
  const activeFamilyId = localStorage.getItem('familyId') || null;

  return (
    <div style={{ height: 'calc(100vh - 220px)', minHeight: 500 }}>
      <FamilyTreeBalkan
        ref={treeRef}
        onPersonClick={onPersonClick}
        onAddRelative={onAddRelative}
        activeFamilyId={activeFamilyId}
      />
    </div>
  );
}

function FamilyHub() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'members') // 'members' or 'tree'
  const [activeFilter, setActiveFilter] = useState('all')
  const [viewMode, setViewMode] = useState('photo')
  const [searchQuery, setSearchQuery] = useState('')
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userFamily, setUserFamily] = useState(null)
  const [isLoadingFamily, setIsLoadingFamily] = useState(true)
  const [familyMembers, setFamilyMembers] = useState([])
  const [treeOnlyPeople, setTreeOnlyPeople] = useState([])
  const [isLoadingMembers, setIsLoadingMembers] = useState(true)
  const [personId, setPersonId] = useState(null)

  // Grouped family data for household view
  const [groupedFamily, setGroupedFamily] = useState(null)
  const [isLoadingGrouped, setIsLoadingGrouped] = useState(true)

  // Context menu state for TreeNodeMenu
  const [isNodeMenuOpen, setIsNodeMenuOpen] = useState(false)
  const [nodeMenuPosition, setNodeMenuPosition] = useState({ x: 0, y: 0 })
  const [selectedMemberData, setSelectedMemberData] = useState(null)
  const [anchorPersonName, setAnchorPersonName] = useState('')
  const [selectedRelationshipType, setSelectedRelationshipType] = useState(null)

  // Member detail panel state
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false)
  const [detailPanelMember, setDetailPanelMember] = useState(null)

  // Tree ref for refreshing after modal add
  const treeRef = React.useRef(null)

  // Person selected from tree add (for modal pre-fill)
  const [treeAddPersonId, setTreeAddPersonId] = useState(null)

  // Tree refresh trigger
  const [treeRefreshKey, setTreeRefreshKey] = useState(0)

  // Cross-family tracking state
  const [spouseInvited, setSpouseInvited] = useState(false)
  const [addedChildIds, setAddedChildIds] = useState(new Set())

  const navigate = useNavigate()
  const ls = new SecureLS({ encodingType: 'aes' })

  // Get the actual family ID from user data or API
  const getFamilyId = () => {
    try {
      const familyId = localStorage.getItem('familyId')
      if (familyId) return familyId

      const userData = getUser() || {}
      if (userData.family?.id) {
        return userData.family.id
      }

      return null
    } catch (error) {
      console.error('Error getting family ID:', error)
      return null
    }
  }

  const familyId = userFamily?.id || getFamilyId()

  // Get current user's person ID
  const getUserPersonId = () => {
    try {
      const userData = getUser() || {}
      return userData.person_id || userData.id || null
    } catch (error) {
      console.error('Error getting user person ID:', error)
      return null
    }
  }

  const userPersonId = getUserPersonId()

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = () => {
      const loggedIn = localStorage.getItem('isLoggedIn') === 'true'
      const token = ls.get('authToken')
      setIsAuthenticated(loggedIn && !!token)
    }
    checkAuth()
  }, [])

  // Fetch person_id from API if not in localStorage
  useEffect(() => {
    const fetchPersonId = async () => {
      try {
        const localPersonId = getUserPersonId()
        if (localPersonId) {
          setPersonId(localPersonId)
          return
        }

        const token = ls.get('authToken')
        if (!token) {
          return
        }

        const userData = getUser() || {}
        if (userData.id) {
          const response = await axiosInstance.get(`/genealogy/people/?user_id=${userData.id}`)
          if (response.data && response.data.results && response.data.results.length > 0) {
            const fetchedPersonId = response.data.results[0].id
            setPersonId(fetchedPersonId)

            userData.person_id = fetchedPersonId
            setUser(userData)
          }
        }
      } catch (error) {
        console.error('Error fetching person_id:', error)
      }
    }

    if (isAuthenticated) {
      fetchPersonId()
    }
  }, [isAuthenticated])

  // Fetch user's family
  const fetchUserFamily = async () => {
    try {
      setIsLoadingFamily(true)

      const response = await axiosInstance.get('/families/')

      const families = response.data.results || response.data || []
      if (families.length > 0) {
        // Use the active family from localStorage if it matches one of the user's families
        const activeFamilyId = localStorage.getItem('familyId')
        const activeFamily = activeFamilyId
          ? families.find(f => f.id === activeFamilyId)
          : null

        if (activeFamily) {
          setUserFamily(activeFamily)
        } else {
          // Fallback to first family only if no active family is set
          setUserFamily(families[0])
          localStorage.setItem('familyId', families[0].id)
        }
      } else {
        // Use fallback family ID from localStorage if available
        const fallbackFamilyId = getFamilyId()
        if (fallbackFamilyId) {
          setUserFamily({
            id: fallbackFamilyId,
            name: 'Development Family',
            isFallback: true
          })
        }
      }
    } catch (error) {
      console.error('Error fetching user family:', error)
    } finally {
      setIsLoadingFamily(false)
    }
  }

  // Fetch family members
  const fetchFamilyMembers = async (familyId) => {
    if (!familyId) {
      setIsLoadingMembers(false)
      return
    }

    try {
      setIsLoadingMembers(true)

      const response = await axiosInstance.get(`/families/${familyId}/members/`)

      // Handle different response formats
      const membersData = response.data.members || response.data.results || response.data || []

      // Transform API data to component format
      const transformedMembers = membersData.map(member => {
        const age = member.date_of_birth ? calculateAge(member.date_of_birth) : null
        const isMinor = member.is_minor !== undefined ? member.is_minor : (age !== null && age < 16)

        return {
          id: member.id,
          name: `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown',
          firstName: member.first_name,
          lastName: member.last_name,
          gender: member.gender || 'unknown',
          living: member.living !== false,
          birthDate: member.date_of_birth,
          deathDate: member.date_of_death,
          photo: member.photo || (member.gender === 'M' ? '👨' : member.gender === 'F' ? '👩' : '👤'),
          relationship: member.role || 'Family member',
          storyCount: member.story_count || 0,
          photoCount: member.photo_count || 0,
          voiceCount: member.voice_count || 0,
          age,
          isMinor,
          email: member.email,
          phone: member.phone,
          hasAccount: member.has_account,
          invitationAccepted: member.invitation_accepted,
          joinedAt: member.joined_at,
          nodeState: member.node_state || 'member',
          graceDay: member.grace_day || null,
        }
      })

      setFamilyMembers(transformedMembers)
      setTreeOnlyPeople(response.data.tree_only || [])
    } catch (error) {
      console.error('Error fetching family members:', error)
      if (error.response) {
        console.error('Error response:', error.response.status, error.response.data)
      }
    } finally {
      setIsLoadingMembers(false)
    }
  }

  // Helper function to calculate age from birth date
  const calculateAge = (birthDate) => {
    if (!birthDate) return null
    try {
      const birth = new Date(birthDate)
      const today = new Date()
      let age = today.getFullYear() - birth.getFullYear()
      const monthDiff = today.getMonth() - birth.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--
      }
      return age
    } catch (error) {
      return null
    }
  }

  // Fetch family data when component mounts and user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchUserFamily()
    }
  }, [isAuthenticated])

  // Fetch family members when family is loaded
  useEffect(() => {
    if (userFamily?.id && isAuthenticated) {
      fetchFamilyMembers(userFamily.id)
      fetchGroupedFamily()
    }
  }, [userFamily, isAuthenticated])

  // Fetch grouped family data for household view
  const fetchGroupedFamily = async () => {
    try {
      setIsLoadingGrouped(true)
      // Debug: Check token availability
      const token = ls.get('authToken')

      if (!token) {
        console.error('No auth token available - request will fail!')
        setGroupedFamily(null)
        return
      }

      // Explicitly pass token in headers to ensure it's sent
      const response = await axiosInstance.get('/genealogy/people/my_family_grouped/', {
        headers: {
          'Authorization': `Token ${token}`
        }
      })
      setGroupedFamily(response.data)
    } catch (error) {
      console.error('Error fetching grouped family:', error)
      if (error.response) {
        console.error('Response status:', error.response.status)
        console.error('Response data:', error.response.data)

        // If 401, check token state again
        if (error.response.status === 401) {
          const tokenCheck = ls.get('authToken')
          console.error('Token at error time:', !!tokenCheck)
          console.error('This may be a timing issue - token not available when request was made')
        }
      }
      // Set a minimal fallback structure so the UI still renders
      setGroupedFamily(null)
    } finally {
      setIsLoadingGrouped(false)
    }
  }

  // Calculate family stats
  const familyStats = {
    totalMembers: familyMembers.length,
    activeMembers: familyMembers.filter(member => member.living).length,
    adults: familyMembers.filter(member => member.living && !member.isMinor && member.hasAccount).length,
    minors: familyMembers.filter(member => member.living && member.isMinor).length,
    pendingInvites: familyMembers.filter(member => !member.hasAccount && !member.invitationAccepted && !member.isMinor).length,
    treeOnly: familyMembers.filter(member => !member.hasAccount && !member.isMinor && member.invitationAccepted).length,
    totalStories: familyMembers.reduce((sum, m) => sum + (m.storyCount || 0), 0),
    photosShared: familyMembers.reduce((sum, m) => sum + (m.photoCount || 0), 0),
    voiceMemories: familyMembers.reduce((sum, m) => sum + (m.voiceCount || 0), 0),
  }

  // Filter members by search query
  const filteredMembers = familyMembers.filter(member =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Apply active filter
  const getFilteredMembers = () => {
    switch (activeFilter) {
      case 'active':
        return filteredMembers.filter(member => member.living)
      default:
        return filteredMembers
    }
  }

  const displayMembers = getFilteredMembers()

  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId)
  }

  const handleSearch = (e) => {
    setSearchQuery(e.target.value)
  }

  // Called when user clicks add (mother/father/etc) on the Balkan tree
  const handleTreeAddRelative = ({ personId, personName, relationship }) => {
    setTreeAddPersonId(personId)
    setAnchorPersonName(personName)
    // Map relationship name to the ID used by InviteFamilyModal
    const relMap = {
      'mother': 'Mother', 'father': 'Father',
      'wife': 'Wife', 'husband': 'Husband',
      'son': 'Son', 'daughter': 'Daughter',
    }
    setSelectedRelationshipType(relMap[relationship] || relationship)
    setIsInviteModalOpen(true)
  }

  const handleCloseInviteModal = (memberWasAdded = false) => {
    setIsInviteModalOpen(false)
    // Reset context-based adding state
    setAnchorPersonName('')
    setSelectedRelationshipType(null)
    setSelectedMemberData(null)
    setTreeAddPersonId(null)
    if (memberWasAdded && familyId) {
      fetchFamilyMembers(familyId)
      fetchGroupedFamily()
      setTreeRefreshKey(k => k + 1)
      // Refresh the Balkan tree
      if (treeRef.current) {
        treeRef.current.refreshTree()
      }
    }
  }

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setSearchParams({ tab })
  }

  const handleEditMember = (e, memberId) => {
    e.stopPropagation()
    navigate(`/family-hub/member/${memberId}`)
  }

  // Open the context menu when clicking on a member card
  const openNodeMenu = (memberData, position) => {
    setSelectedMemberData(memberData)
    setNodeMenuPosition(position)
    setIsNodeMenuOpen(true)
  }

  // Close the context menu
  const closeNodeMenu = () => {
    setIsNodeMenuOpen(false)
    setSelectedMemberData(null)
  }

  // Handle adding a relative from the context menu
  const handleAddRelative = (memberId, relationshipCategory, memberName) => {

    // Map relationship category to relationship type name
    const categoryToRelationshipMap = {
      'spouse': 'Spouse',
      'child': 'Son',  // Default to Son, user can change in modal
      'sibling': 'Brother', // Default to Brother, user can change in modal
      'parent': 'Father'  // Default to Father, user can change in modal
    }

    // Set the anchor person name for display in modal
    setAnchorPersonName(memberName)
    setSelectedRelationshipType(categoryToRelationshipMap[relationshipCategory])

    // Close the context menu and open the invite modal
    closeNodeMenu()
    setIsInviteModalOpen(true)
  }

  // Handle viewing a person's profile from context menu
  const handleViewPerson = (memberId) => {
    closeNodeMenu()
    navigate(`/family-hub/member/${memberId}`)
  }

  // Handle editing a person's details from context menu
  const handleEditPersonFromMenu = (memberId) => {
    closeNodeMenu()
    navigate(`/family-hub/member/${memberId}`)
  }

  // Handle member card click - open detail panel
  const handleMemberCardClick = (e, member) => {
    e.preventDefault()
    e.stopPropagation()

    // For non-self members, navigate to their profile page
    const selfId = groupedFamily?.me?.id
    if (member.id && member.id !== selfId) {
      navigate(`/family-hub/member/${member.id}`)
      return
    }

    // For self, navigate to My Life
    navigate('/my-life')
  }

  // Close detail panel
  const handleCloseDetailPanel = () => {
    setIsDetailPanelOpen(false)
    setDetailPanelMember(null)
  }

  // Invite cross-family spouse to this family
  const handleInviteSpouse = async () => {
    if (!groupedFamily?.spouse || !familyId) return
    const spouse = groupedFamily.spouse

    try {
      // Optimistic update
      setSpouseInvited(true)

      // Look up the "spouse" relationship type ID from genealogy types
      const rtResponse = await axiosInstance.get('/genealogy/relationship-types/')
      const rtList = rtResponse.data?.results || rtResponse.data || []
      const spouseType = rtList.find(rt =>
        rt.name?.toLowerCase() === 'spouse'
      )

      if (!spouseType) {
        throw new Error('Could not find spouse relationship type')
      }

      await axiosInstance.post(`/families/${familyId}/invite_member/`, {
        invited_first_name: spouse.first_name,
        invited_last_name: spouse.last_name || '',
        invited_email: spouse.email || '',
        relationship_type: spouseType.id,
        person_id: spouse.id,
      })

      // Refresh data
      fetchGroupedFamily()
    } catch (error) {
      console.error('Error inviting spouse:', error)
      setSpouseInvited(false)
    }
  }

  // Add child to spouse's family
  const handleAddChildToFamily = async (childId, targetFamilyId) => {
    try {
      // Optimistic update
      setAddedChildIds(prev => new Set([...prev, childId]))

      await axiosInstance.post('/genealogy/people/add_to_family/', {
        person_id: childId,
        family_id: targetFamilyId,
      })

      // Refresh data
      fetchGroupedFamily()
    } catch (error) {
      console.error('Error adding child to family:', error)
      setAddedChildIds(prev => {
        const next = new Set(prev)
        next.delete(childId)
        return next
      })
    }
  }

  return (
    <HubLayout>
      {/* Senior-Friendly Cream Background */}
      <div className="min-h-full">
        {/* Page Header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', padding: '32px 40px 0' }}>
          <div>
            <h1 style={{ fontFamily: "'Lora', Georgia, serif", fontWeight: 700, fontSize: 30, color: '#2C2F26', lineHeight: 1.1, margin: 0 }}>
              My Family
            </h1>
            <p style={{ fontSize: 15, color: '#5A6150', marginTop: 7 }}>
              Everyone whose stories live in your shared family album.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            {/* Segmented Tabs */}
            <div style={{ background: '#EDE8CD', borderRadius: 10, padding: 4, display: 'inline-flex', gap: 2 }}>
              <button
                onClick={() => handleTabChange('members')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '8px 16px', borderRadius: 7, fontSize: 14, fontWeight: activeTab === 'members' ? 700 : 600,
                  background: activeTab === 'members' ? '#fff' : 'transparent',
                  color: activeTab === 'members' ? '#556B2F' : '#5A6150',
                  border: 'none', cursor: 'pointer',
                  boxShadow: activeTab === 'members' ? '0 1px 2px rgba(54,60,40,.1)' : 'none',
                }}
              >
                <Users size={15} /> Members
              </button>
              <button
                onClick={() => handleTabChange('tree')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '8px 16px', borderRadius: 7, fontSize: 14, fontWeight: activeTab === 'tree' ? 700 : 600,
                  background: activeTab === 'tree' ? '#fff' : 'transparent',
                  color: activeTab === 'tree' ? '#556B2F' : '#5A6150',
                  border: 'none', cursor: 'pointer',
                  boxShadow: activeTab === 'tree' ? '0 1px 2px rgba(54,60,40,.1)' : 'none',
                }}
              >
                <GitBranch size={15} /> Family Tree
              </button>
            </div>
            {/* Invite Button */}
            <button
              onClick={() => setIsInviteModalOpen(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: '#556B2F', color: '#fff', padding: '11px 20px',
                borderRadius: 8, fontWeight: 700, fontSize: 14, border: 'none',
                cursor: 'pointer', boxShadow: '0 1px 2px rgba(54,60,40,.12)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#3E4F22'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#556B2F'; }}
            >
              <UserPlus size={16} /> Invite Family
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'members' ? (
          <FamilyHubMembers
            groupedFamily={groupedFamily}
            familyMembers={familyMembers}
            treeOnlyPeople={treeOnlyPeople}
            familyStats={familyStats}
            userPersonId={userPersonId}
            onInvite={() => setIsInviteModalOpen(true)}
            onAddChild={() => setIsInviteModalOpen(true)}
          />
        ) : (
          /* Tree Tab Content */
          <div className="pb-8">
            <FamilyTreeWrapper
              key={treeRefreshKey}
              treeRef={treeRef}
              onPersonClick={(personId) => {
                const selfId = groupedFamily?.me?.id
                if (personId === selfId) {
                  navigate('/my-life')
                } else {
                  navigate(`/family-hub/member/${personId}`)
                }
              }}
              onAddRelative={handleTreeAddRelative}
            />
          </div>
        )}
      </div>

      {/* Mobile FAB — Invite button */}
      {canInviteMembers() && activeTab === 'members' && (
        <button
          onClick={() => navigate('/family-hub/invite')}
          className="sm:hidden fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full bg-[#E65100] text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Invite family member"
        >
          <UserPlus className="w-6 h-6" />
        </button>
      )}

      {/* Tree Node Context Menu */}
      <TreeNodeMenu
        isOpen={isNodeMenuOpen}
        onClose={closeNodeMenu}
        position={nodeMenuPosition}
        personName={selectedMemberData?.name || ''}
        personId={selectedMemberData?.id || ''}
        hasSpouse={false}
        onAddRelative={handleAddRelative}
        onEdit={handleEditPersonFromMenu}
        onView={handleViewPerson}
      />

      {/* Member Detail Panel */}
      <MemberDetailPanel
        isOpen={isDetailPanelOpen}
        onClose={handleCloseDetailPanel}
        member={detailPanelMember}
        isChild={detailPanelMember?.is_minor || detailPanelMember?.age < 18}
      />

      {/* Invite Family Modal */}
      {isInviteModalOpen && (
        <InviteFamilyModal
          isOpen={isInviteModalOpen}
          onClose={handleCloseInviteModal}
          familyId={familyId}
          familyName={userFamily?.name}
          anchorPersonName={anchorPersonName}
          selectedPersonId={treeAddPersonId}
          selectedRelationshipType={selectedRelationshipType}
          onSuccess={() => handleCloseInviteModal(true)}
        />
      )}
    </HubLayout>
  )
}

export default FamilyHub
